'use client';
import LazyLoadImage from '@repo/ui/src/components/LazyLoadImage';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import dynamic from 'next/dynamic';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import { useIsMobile } from '@repo/ui/src/utils/useMediaQuery';
import { MediaType, LinkProps } from '@repo/ui/src/types';

const Markdown = dynamic(() => import('@repo/ui/src/components/Markdown'));

interface TileType {
  image?: Array<MediaType>;
  instagramHandle?: string;
  imgLink?: Array<LinkProps>;
  [key: string]: any;
}

interface PhotoGalleryProps {
  title?: string;
  markdownDescription?: string;
  body: TileType[];
  _testID?: string;
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({
  title,
  markdownDescription,
  body,
  _testID,
}) => {
  const { push } = useModal(ModalID.image);
  const isMobile = useIsMobile();
  const photoQty = body?.length;

  if (!body.length) {
    return null;
  }

  // Grid layouts based on number of photos
  const GRID_LAYOUTS = {
    1: 'grid grid-cols-1 max-w-md mx-auto',
    2: 'grid grid-cols-1 md:grid-cols-2 max-w-xl md:max-w-3xl mx-auto',
    3: 'grid grid-cols-2 md:grid-cols-3 gap-2',
    4: 'grid grid-cols-2 gap-2',
    5: 'grid grid-cols-2 sm:grid-cols-5 gap-2',
    6: 'grid grid-cols-2 md:grid-cols-3 gap-2',
    7: 'grid grid-cols-2 md:grid-cols-3 gap-2',
    8: 'grid grid-cols-2 md:grid-cols-4 gap-2',
    9: 'grid grid-cols-2 md:grid-cols-3 gap-2',
    default: 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2',
  };

  // Get the appropriate grid layout
  const gridLayout = GRID_LAYOUTS[photoQty as keyof typeof GRID_LAYOUTS] || GRID_LAYOUTS.default;

  return (
    <div
      className="flex justify-center text-center md:px-8 py-8 md:py-16 rotating-background"
      tabIndex={1}
      data-test-id={`photo_gallery_${_testID}`}
    >
      <div className="px-4">
        {title && <h2 className="tra-text-web-heading-3">{title}</h2>}
        {markdownDescription && (
          <Markdown className="text-center mb-6" bodySize="xLarge">
            {markdownDescription}
          </Markdown>
        )}

        <div
          className={`${gridLayout} mx-auto`}
          style={{ maxWidth: isMobile ? '370px' : '1200px' }}
        >
          {body.map((tile, i) => {
            const imgUrl = tile?.image && buildImageSrc(tile.image[0], 470, 1, 1);
            const instagramHandle = tile?.instagramHandle;
            const image = tile?.image?.[0];
            const imgLink = tile.imgLink;

            return (
              <div key={i} className={`min-h-40 overflow-hidden`}>
                <button
                  onClick={() => push({ instagramHandle, image, imgLink })}
                  className="group w-full h-full relative focus:outline-brand-300 focus:border-2"
                >
                  <LazyLoadImage
                    imgComponent={
                      <img
                        src={imgUrl}
                        alt={
                          tile.image && tile.image.length && tile.image[0].name
                            ? tile.image[0].name
                            : instagramHandle || 'carousel-image'
                        }
                        className="w-full h-full object-cover cursor-pointer"
                      />
                    }
                    className="h-full"
                  />
                  <div className="overlay items-center justify-center hidden group-hover:flex group-focus:flex absolute top-0 bottom-0 left-0 right-0 bg-black/50 cursor-pointer">
                    <span>
                      {tile?.instagramHandle && (
                        <p className="tra-text-web-body-l text-center text-white underline">
                          {tile.instagramHandle}
                        </p>
                      )}
                      <p className="tra-text-web-body-l text-center text-white underline">View</p>
                    </span>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PhotoGallery;
